import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  Container,
  Grid,
} from '@mui/material';
import {
  Store as StoreIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import config from '../config';
import { getValidToken } from '../services/tokenRefreshService';
import { useStoreVisibility } from '../hooks/useStoreVisibility';

interface WorkingHours {
  dayOfWeek: number;
  slotNumber: number;
  openTime: string;
  closeTime: string;
}

interface EarlyCutoff {
  dayOfWeek: number;
  cutoffTime: string;
}

interface Store {
  id: string;
  name: string;
  externalId: string;
  isActive: boolean;
  hasCustomSettings: boolean;
  usesDefaultHours: boolean;
  workingHours: WorkingHours[];
  earlyCutoffs: EarlyCutoff[];
  systemDefaults: {
    workingHours: WorkingHours[];
  };
  pickupNotes: string | null;
  usesDefaultUberSettings: boolean;
}

export function StoreSelector() {
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isStoreVisible } = useStoreVisibility();

  const visibleStores = useMemo(() => allStores.filter(isStoreVisible), [allStores, isStoreVisible]);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/stores?includeHidden=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch stores');
      }

      const data = await res.json();
      setAllStores(data);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stores');
      setLoading(false);
    }
  };

  // Helper: Check if store is currently open
  const isStoreOpen = (store: Store): boolean => {
    const now = new Date();
    const saTime = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
    const currentDay = saTime.getDay(); // 0 = Sunday, 6 = Saturday
    const currentTime = saTime.toTimeString().slice(0, 8); // HH:MM:SS

    const hours = store.usesDefaultHours ? store.systemDefaults.workingHours : store.workingHours;
    const todayHours = hours.filter(h => h.dayOfWeek === currentDay);

    return todayHours.some(slot => currentTime >= slot.openTime && currentTime <= slot.closeTime);
  };

  // Helper: Check if past cutoff time for today
  const isPastCutoff = (store: Store): boolean => {
    if (!store.earlyCutoffs || store.earlyCutoffs.length === 0) return false;

    const now = new Date();
    const saTime = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
    const currentDay = saTime.getDay();
    const currentTime = saTime.toTimeString().slice(0, 8);

    const todayCutoff = store.earlyCutoffs.find(c => c.dayOfWeek === currentDay);
    return todayCutoff ? currentTime > todayCutoff.cutoffTime : false;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} sx={{ color: 'primary.main', mb: 1 }}>
            🏬 Select a Store
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose a store to manage working hours, liquor cutoff times, and store-specific settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/stores/defaults')}
          sx={{ fontWeight: 600 }}
        >
          Manage System Defaults
        </Button>
      </Box>

      <Grid container spacing={3}>
        {visibleStores.map((store) => {
          const open = isStoreOpen(store);
          const pastCutoff = isPastCutoff(store);
          const hasCutoffs = store.earlyCutoffs && store.earlyCutoffs.length > 0;

          // Check if store is using all defaults
          const usesAllDefaults = store.usesDefaultHours && !store.hasCustomSettings && store.usesDefaultUberSettings;

          // Check if this is the primary Diep River store
          const isDiepRiver = store.name.toLowerCase().includes('diep river') && !store.name.toLowerCase().includes('skc');

          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={store.id}>
              <Card
                sx={{
                  height: '100%',
                  border: '2px solid',
                  borderColor: store.isActive ? (open ? 'success.main' : 'grey.300') : 'error.light',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    borderColor: 'primary.main',
                  },
                  // Subtle glow for Diep River (primary store)
                  ...(isDiepRiver && {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 20px rgba(255, 23, 68, 0.25)',
                    '&:hover': {
                      boxShadow: '0 0 30px rgba(255, 23, 68, 0.35)',
                      transform: 'translateY(-4px)',
                    },
                  }),
                }}
                onClick={() => navigate(`/stores/${store.id}/settings`)}
              >
                <CardContent>
                  <Stack spacing={2}>
                    {/* Store Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          backgroundColor: store.isActive ? 'primary.main' : 'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <StoreIcon />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {store.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {store.externalId}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Status Chips */}
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      <Chip
                        icon={store.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={store.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={store.isActive ? 'success' : 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        icon={open ? <CheckCircleIcon /> : <CancelIcon />}
                        label={open ? 'OPEN' : 'CLOSED'}
                        size="small"
                        color={open ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />

                      {/* Show consolidated badge if using all defaults, otherwise show individual badges */}
                      {usesAllDefaults ? (
                        <Chip
                          icon={<SettingsIcon />}
                          label="Using Default Settings"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ) : (
                        <>
                          {!store.usesDefaultHours && (
                            <Chip
                              icon={<ScheduleIcon />}
                              label="Custom Hours"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {store.hasCustomSettings && (
                            <Chip
                              icon={<SettingsIcon />}
                              label="Custom Pricing"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {!store.usesDefaultUberSettings && (
                            <Chip
                              icon={<LocalShippingIcon />}
                              label="Custom Uber Settings"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </>
                      )}

                      {hasCutoffs && (
                        <Chip
                          icon={pastCutoff ? <WarningIcon /> : <ScheduleIcon />}
                          label={pastCutoff ? 'Past Liquor Cutoff' : 'Has Liquor Cutoff'}
                          size="small"
                          color={pastCutoff ? 'error' : 'info'}
                          variant="outlined"
                        />
                      )}

                      {!store.pickupNotes && (
                        <Chip
                          icon={<WarningIcon />}
                          label="No Pickup Notes"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {visibleStores.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No stores found. Please contact your administrator.
        </Alert>
      )}
    </Container>
  );
}
